/**
 * useSearch Hook
 * 
 * Provides search functionality across parent reference words and study guides
 * Extracted from App.jsx to improve code organization
 */

import { useMemo } from 'react';

/**
 * Custom hook for searching parent reference words and study guides
 * @param {Object} options - Search options
 * @param {string} options.searchQuery - Search query string
 * @param {Object} options.parentReference - Parent reference data
 * @param {Object} options.studyGuides - Study guides data
 * @param {number} options.maxResults - Maximum number of results (default: 10)
 * @returns {Array} Array of search results
 */
export const useSearch = ({
  searchQuery,
  parentReference = {},
  studyGuides = {},
  maxResults = 10
}) => {
  const searchResults = useMemo(() => {
    if (!searchQuery || !searchQuery.trim()) return [];
    
    const results = [];
    const query = searchQuery.toLowerCase();
    
    // Search in parent reference words
    Object.entries(parentReference).forEach(([subjectKey, subject]) => {
      if (!subject || !subject.categories) return;
      
      subject.categories.forEach(category => {
        if (!category || !category.words) return;
        
        category.words.forEach(wordItem => {
          const word = typeof wordItem === 'string' ? wordItem : wordItem.word;
          if (word && word.toLowerCase().includes(query)) {
            results.push({
              type: 'word',
              subject: subject.title,
              category: category.name,
              word: word,
              example: typeof wordItem === 'object' ? wordItem.example : null,
              usage: typeof wordItem === 'object' ? wordItem.usage : null
            });
          }
        });
      });
    });
    
    // Search in study guides
    Object.entries(studyGuides).forEach(([key, guide]) => {
      if (guide && guide.title && guide.title.toLowerCase().includes(query)) {
        results.push({
          type: 'subject',
          subject: guide.title,
          icon: guide.icon,
          screen: key
        });
      }
    });
    
    return results.slice(0, maxResults);
  }, [searchQuery, parentReference, studyGuides, maxResults]);

  return searchResults;
};

export default useSearch;

